/**
 * React DOM Error Fix for Google Translate Conflicts
 * Production-ready solution that patches DOM methods to handle Google Translate modifications
 */

class ReactDOMErrorFix {
    private static instance: ReactDOMErrorFix;
    private originalRemoveChild: typeof Node.prototype.removeChild;
    private originalInsertBefore: typeof Node.prototype.insertBefore;
    private originalAppendChild: typeof Node.prototype.appendChild;
    private isPatched: boolean = false;

    private constructor() {
        this.originalRemoveChild = Node.prototype.removeChild;
        this.originalInsertBefore = Node.prototype.insertBefore;
        this.originalAppendChild = Node.prototype.appendChild;
        this.patchDOMMethods();
    }

    static getInstance(): ReactDOMErrorFix {
        if (!ReactDOMErrorFix.instance) {
            ReactDOMErrorFix.instance = new ReactDOMErrorFix();
        }
        return ReactDOMErrorFix.instance;
    }

    /**
     * Patch DOM methods to handle Google Translate conflicts
     */
    private patchDOMMethods(): void {
        if (this.isPatched) return;
        
        const self = this;
        
        // Patch removeChild to handle the error gracefully
        (Node.prototype as any).removeChild = function(child: Node): Node {
            try {
                // Check if the child is actually a child of this node
                if (child && child.parentNode === this) {
                    return self.originalRemoveChild.call(this, child);
                }
                
                // If not, try to find and remove it from the actual parent
                if (child && child.parentNode) {
                    console.warn('React tried to remove a node from wrong parent, fixing...');
                    return child.parentNode.removeChild(child);
                }
                
                // If the child has no parent, it's already been removed (probably by Google Translate)
                console.warn('Node already removed from DOM, skipping removeChild');
                return child;
                
            } catch (error) {
                // Log the error but don't throw - this prevents React from crashing
                console.warn('removeChild error caught and handled:', error);
                
                // Try alternative removal methods
                if (child && child.parentNode) {
                    try {
                        child.parentNode.removeChild(child);
                    } catch (e) {
                        // Node was already removed
                    }
                }
                
                return child;
            }
        };

        // Patch insertBefore to handle conflicts
        (Node.prototype as any).insertBefore = function(newChild: Node, refChild: Node | null): Node {
            try {
                // Validate that refChild is actually a child of this node
                if (refChild && refChild.parentNode !== this) {
                    console.warn('Reference node is not a child of this parent, appending instead');
                    return self.originalAppendChild.call(this, newChild);
                }
                
                return self.originalInsertBefore.call(this, newChild, refChild);
            } catch (error) {
                console.warn('insertBefore error caught and handled:', error);
                // Fallback to appendChild
                try {
                    return self.originalAppendChild.call(this, newChild);
                } catch (e) {
                    console.warn('Failed to insert node:', e);
                    return newChild;
                }
            }
        };

        // Also patch appendChild for completeness
        (Node.prototype as any).appendChild = function(newChild: Node): Node {
            try {
                // Remove the child from its current parent if it has one
                if (newChild && newChild.parentNode && newChild.parentNode !== this) {
                    newChild.parentNode.removeChild(newChild);
                }
                return self.originalAppendChild.call(this, newChild);
            } catch (error) {
                console.warn('appendChild error caught and handled:', error);
                return newChild;
            }
        };

        this.isPatched = true;
    }

    /**
     * Additional protection specifically for modal and toast operations
     */
    public protectModalOperations(): void {
        // Override React's error handling for specific scenarios
        const originalConsoleError = console.error;
        console.error = function(...args: any[]) {
            const errorMessage = args[0]?.toString() || '';
            
            // Suppress the specific removeChild error that occurs with Google Translate
            if (errorMessage.includes("Failed to execute 'removeChild'") ||
                errorMessage.includes("The node to be removed is not a child")) {
                console.warn('Suppressed removeChild error caused by Google Translate');
                return;
            }
            
            // Let other errors through
            originalConsoleError.apply(console, args);
        };
    }

    /**
     * Clean up and restore original methods
     */
    public destroy(): void {
        if (!this.isPatched) return;
        
        Node.prototype.removeChild = this.originalRemoveChild;
        Node.prototype.insertBefore = this.originalInsertBefore;
        Node.prototype.appendChild = this.originalAppendChild;
        this.isPatched = false;
    }
}

// Initialize immediately
const errorFix = ReactDOMErrorFix.getInstance();
errorFix.protectModalOperations();

// Also add a global error handler as a last resort
window.addEventListener('error', (event) => {
    if (event.error?.message?.includes("Failed to execute 'removeChild'")) {
        console.warn('Global error handler caught removeChild error, preventing app crash');
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
}, true);

// Handle unhandled promise rejections related to DOM
window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.message?.includes("Failed to execute 'removeChild'")) {
        console.warn('Caught unhandled removeChild promise rejection');
        event.preventDefault();
        return false;
    }
});

export default errorFix;